<template>
  <div class="flex h-[67vh] gap-[60px] overflow-y-auto px-6 py-3 text-center">
    <SideBar
      :selected-services="selectedServices"
      :services="ref_services"
      :dirty-services="dirtyServicesArray"
      @value-change="(val) => selectService(val)"
    />
    <div
      v-for="service in ref_services"
      :key="service.label"
      class="w-full"
      :class="visibleService.label == service.label ? 'block' : 'hidden'"
    >
      <p class="text-heading-title mb-3 text-left">{{ service.label }}</p>
      <p class="text-body-default mb-6 text-left">{{ service.description }}</p>
      <div class="mb-6 w-full">
        <SettingsToggle
          :data-service-toggle="`services_${returnKey(service)}`"
          :name="`services.${returnKey(service)}.isSet`"
          :text="t('add_unit_to_shipment', { unit: service.label })"
          :disabled="service.carrier_key === 'dangerous_goods' && isDangerousGoodsLocked"
          @update:model-value="(val) => addValidationRules(val, service)"
        />
      </div>
      <div v-if="service.type == 'select' && selectedServices.includes(returnKey(service))" class="mb-6 w-full">
        <SelectComponent :service="service" @date-update="(val) => emit('dateUpdate', val)" />
      </div>
      <div v-if="service.type == 'dot_delivery' && selectedServices.includes(returnKey(service))" class="mb-6 w-full">
        <DotDeliveryComponent :service="service" @date-update="(val) => emit('dateUpdate', val)" />
      </div>
      <div v-if="service.type == 'multi_select' && selectedServices.includes(returnKey(service))" class="mb-6 w-full">
        <MultiSelectComponent :service="service" />
      </div>
      <div
        v-if="service.type == 'field_array' && selectedServices.includes(returnKey(service))"
        class="mb-6 flex w-full gap-5"
      >
        <div v-for="field in service.fields" :key="field.key" class="max-w-[100px] text-left">
          <InputText
            class="w-full"
            type="number"
            :name="`services.${returnKey(service)}.${field.key}`"
            :label="field.label"
            suffix="stk."
          />
        </div>
      </div>
      <div v-if="service.type == 'object_array' && selectedServices.includes(returnKey(service))" class="mb-6 w-full">
        <ObjectComponent :service="service" />
      </div>
      <div v-if="service.link">
        <p class="text-body-large mb-3 text-left font-medium">{{ t("useful_links") }}</p>
        <a :href="JSON.parse(service.link)" target="_blank" rel="noopener noreferrer">
          <Button class="mb-2 flex w-full max-w-max" type="button" :variant="ButtonVariant.Ghost" text-left>
            {{ t("read_more_about", { entity: service.label.toLocaleLowerCase() }) }}
            <Icon class="mt-0.5" src="arrow_up_right" />
          </Button>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import DotDeliveryComponent from "./extraServiceComponents/dotDeliveryComponent.vue";
  import MultiSelectComponent from "./extraServiceComponents/multiSelectComponent.vue";
  import ObjectComponent from "./extraServiceComponents/objectComponent.vue";
  import SelectComponent from "./extraServiceComponents/selectComponent.vue";
  import SideBar from "./extraServiceComponents/sideBar.vue";

  const { t } = useI18n();
  const yup = useYup();

  const emit = defineEmits(["addExtraService", "dateUpdate", "updateSelectValue", "updateValidation"]);

  const selectedService = ref(null);

  const props = defineProps<{
    services: unknown;
    values: unknown;
    mode: string;
    validationSchema: unknown;
  }>();

  const ref_services = ref(props.services);
  const dirtyServicesArray = ref<string[]>([]);

  const dangerousGoodsService = computed(
    () => ref_services.value?.find((s) => s.carrier_key === "dangerous_goods") ?? null
  );

  const isDangerousGoodsLocked = computed(() => !!props.values.has_dangerous_goods && !!dangerousGoodsService.value);

  const { value: dangerousGoodsIsSet } = useField<boolean>(() => "services.dangerous_goods.isSet", undefined, {
    type: "checkbox",
    checkedValue: true,
    uncheckedValue: false,
    controlled: true,
  });

  const { translateFieldsToValidation } = useValidation();

  watch(
    () => props.services,
    (val) => {
      ref_services.value = val;
    }
  );

  const returnKey = (service: object) => {
    return service.carrier_key == "service_codes" ? service.fields.key : service.carrier_key;
  };

  const selectedServices = computed(() => {
    return Object.keys(props.values.services).filter((key) => {
      return props.values.services[key]?.isSet;
    });
  });

  // Create an async function to update dirtyServicesArray
  const updateDirtyServices = async () => {
    const fields = props.validationSchema[4]?.fields?.services?.fields;
    const values = props.values.services;
    if (!fields) {
      dirtyServicesArray.value = [];
      return;
    }
    const validationPromises = Object.keys(fields)
      .filter((key) => values[key]?.isSet)
      .map(async (key) => {
        const isValid = await fields[key].isValid(props.values.services[key]);
        return { key, isValid };
      });
    const results = await Promise.all(validationPromises);
    dirtyServicesArray.value = results.filter((result) => !result.isValid).map((result) => result.key);
  };

  watch(
    [() => props.validationSchema, () => props.values.services],
    () => {
      nextTick(() => {
        updateDirtyServices();
      });
    },
    { immediate: true, deep: true }
  );

  const visibleService = computed(() => {
    return selectedService.value ? selectedService.value : Object.values(ref_services)[3][0];
  });

  const addValidationRules = async (val, service) => {
    if (["service_codes", "dangerous_goods"].includes(service.carrier_key)) {
      if (val) {
        emit("updateValidation", {
          fields: {
            [service.fields.key]: yup.object({
              isSet: yup.boolean(),
            }),
          },
          step: 3,
        });
      } else {
        emit("updateValidation", {
          fields: {
            [service.fields.key]: yup.object({}),
          },
          step: 3,
        });
      }
    } else {
      const fields = await translateFieldsToValidation(service, val);
      emit("updateValidation", { fields: fields, step: 3 });
    }
  };

  watch(
    isDangerousGoodsLocked,
    (locked) => {
      if (locked) {
        dangerousGoodsIsSet.value = true;
        addValidationRules(true, dangerousGoodsService.value);
      }
    },
    { immediate: true }
  );

  const selectService = (val) => {
    selectedService.value = val;
  };

  onMounted(() => {
    if (selectedServices.value.length > 0) {
      //run addValidationRules for each selected service
      if (props.mode !== "duplicate") {
        return;
      }
      selectedServices.value.forEach(() => {
        addValidationRules(
          true,
          ref_services.value.find((service) => service.carrier_key == returnKey(service))
        );
      });
    }
  });
</script>
